import type { ExpandCursorArea } from '../../recoil/cursor'
import type { RSheetColumn, RSheetRecordNode, RSheetsColumnMeta, RSheetsColumnType } from '../column'
import type { RSheetsCursor, RSheetsCursorPosition } from './cursorTypes'

export const findLeftColumnIndexForSelectAllCells = <T extends RSheetRecordNode>(
  columns: Array<RSheetColumn<T>>,
): number => {
  // 実用的に、左端のシステム系カラムやレコードURLは不要なはずなので、これを除く
  const ignoreColumnTypes = ['rowIndex', 'open', 'checkbox'] as RSheetsColumnType[]
  const ignoreColumnMetaTypes = ['url', 'record_url'] as RSheetsColumnMeta[]

  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: -1 を返すときはどういう意味なんだろう？そういうことは起こりえないということ？
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: selectRowCellMutationも似たようなコードがあるのでまとめられると良い
  return columns.findIndex(
    (c) =>
      !(c.type !== undefined && ignoreColumnTypes.includes(c.type)) &&
      !(c.metaType !== undefined && ignoreColumnMetaTypes.includes(c.metaType)),
  )
}
interface CursorOverlapInfo {
  isMainSelected: boolean
  isExpandSelected: boolean
  isRangeSelected: boolean
}

const getUpAndDownPosition = (positionA: RSheetsCursorPosition, positionB: RSheetsCursorPosition) => {
  if (positionA.rowIndex !== positionB.rowIndex) {
    return {
      upPosition: positionA.rowIndex < positionB.rowIndex ? positionA : positionB,
      downPosition: positionA.rowIndex > positionB.rowIndex ? positionA : positionB,
    }
  }

  return {
    upPosition: positionA.innerRowIndex < positionB.innerRowIndex ? positionA : positionB,
    downPosition: positionA.innerRowIndex > positionB.innerRowIndex ? positionA : positionB,
  }
}
export const getArea = (positionA: RSheetsCursorPosition, positionB: RSheetsCursorPosition): ExpandCursorArea => {
  const { upPosition, downPosition } = getUpAndDownPosition(positionA, positionB)

  return {
    leftColumnIndex: Math.min(positionA.columnIndex, positionB.columnIndex),
    rightColumnIndex: Math.max(positionA.columnIndex, positionB.columnIndex),
    upRowIndex: upPosition.rowIndex,
    downRowIndex: downPosition.rowIndex,
    upInnerRowIndex: upPosition.innerRowIndex,
    downInnerRowIndex: downPosition.innerRowIndex,
  }
}
const isSamePosition = (positionA: RSheetsCursorPosition, positionB: RSheetsCursorPosition) =>
  positionA.rowIndex === positionB.rowIndex &&
  positionA.columnIndex === positionB.columnIndex &&
  positionA.innerRowIndex === positionB.innerRowIndex
export const isCursorOverlap = (
  cursor: RSheetsCursor | undefined,
  position: RSheetsCursorPosition,
): CursorOverlapInfo => {
  const defaultResult = {
    isMainSelected: false,
    isExpandSelected: false,
    isRangeSelected: false,
  }
  const { main, expand } = cursor ?? {}
  if (main === undefined) {
    return defaultResult
  }

  const isMainSelected = isSamePosition(position, main)
  if (expand === undefined) {
    return {
      ...defaultResult,
      isMainSelected,
    }
  }

  const isExpandSelected = position.rowIndex === expand.rowIndex && position.columnIndex === expand.columnIndex
  const {
    leftColumnIndex: left,
    rightColumnIndex: right,
    upRowIndex: up,
    downRowIndex: down,
    upInnerRowIndex,
    downInnerRowIndex,
  } = getArea(main, expand)
  const isParentCellRangeSelected =
    left <= position.columnIndex &&
    position.columnIndex <= right &&
    up <= position.rowIndex &&
    position.rowIndex <= down
  const isRangeSelected =
    isParentCellRangeSelected &&
    // 行境界の最上段でも最下段でもない
    ((up < position.rowIndex && position.rowIndex < down) ||
      // 行境界の最上段で、ネストしたセルの行より下
      (position.rowIndex === up && position.rowIndex !== down && upInnerRowIndex <= position.innerRowIndex) ||
      // 行境界の最下段で、ネストしたセルの行より上
      (position.rowIndex !== up && position.rowIndex === down && position.innerRowIndex <= downInnerRowIndex) ||
      // 1行のみ選択されているとき
      (position.rowIndex === up &&
        position.rowIndex === down &&
        upInnerRowIndex <= position.innerRowIndex &&
        position.innerRowIndex <= downInnerRowIndex))

  return {
    isMainSelected,
    isExpandSelected,
    isRangeSelected,
  }
}
